/* global Platform, Resources, Sys, Services */
Sys.ns("Services");

/**
 * The Game Service.
 *
 * @class Services.Game
 * @extends Services.Base
 */
Services.Game = {
    constructor: function(config) {
        var initResponse,
            parsedResponse,
            urlParameters = Sys.utils.parseUrlParams(window.location.search);

        Services.Game.superclass.constructor.apply(this, arguments);

        initResponse = config.initResponse;
        parsedResponse = config.parsedResponse;

        this._isPlayForFun = initResponse.playforfun;
        this._isGameHistoryAvailable = initResponse.historybutton;
        this._inBaseGame = true;
        this._ready = false;
        this._modes = [Services.storage.readData("playModeManager.modes")];
        this._homeButtonVisible = false;
        this._currentMode = "";
        this._isIdle = false;

        this._isMaxBetAvailable = this._setupMaxBetAvailability(parsedResponse, urlParameters);

        if (config.parsedResponse.g4mode) {
            this._isQuickSpinAvailable = false;
        }
        else if (!(typeof parsedResponse.config === "undefined") && !(typeof parsedResponse.config.gameParameters === "undefined") && !(typeof parsedResponse.config.gameParameters.quickSpinAvailable === "undefined")) {
            this._isQuickSpinAvailable = parsedResponse.config.gameParameters.quickSpinAvailable;
        }
        else {
            this._isQuickSpinAvailable = true;
        }
    },

    _setupEvents: function() {
        this.observable.on({
            "notify:stateHandler.leavingBeforeLoaderCloseState": this._onLoaderClosed.bind(
                this
            ),
            "notify:stateHandler.enteringIdleState": this._onEnteringIdle.bind(
                this
            ),
            "notify:stateHandler.leavingIdleState": this._onLeavingIdle.bind(
                this
            ),
            "notify:gameModeChanged": this._gameModeChanged.bind(this),
            "notify:stateHandler.enteringBonusProgramWidgetState": this._setDisplayMode.bind(this, "TOPBAR"),
            "notify:bonusProgramWidget.reInit": this._setDisplayMode.bind(this, "TOPBAR"),
            "notify:bonusProgramWidget.closed": this._setDisplayMode.bind(this, "DEFAULT"),
            "request:playModeManager.addMode": this.addGameMode.bind(this),
            "service:pluginMenu.opened": this._setDisplayMode.bind(this, "TOPBAR"),
            "service:pluginMenu.closed": this._pluginMenuClosed.bind(this),
            "request:playModeManager.removeMode": this.removeGameMode.bind(this),
            "notify:responseParser.gameConfigurationParsed": this._onGameConfigurationParsed.bind(this)
        });
    },

    /**
     * Checks if the game is ready to be played.
     *
     * @returns {boolean} Whether or not the game is ready to be played.
     */
    isReady: function() {
        return this._ready;
    },

    /**
     * Checks if the game is in play for fun mode.
     *
     * @returns {boolean} Whether or not the game is in play for fun mode.
     */
    isPlayForFun: function() {
        return this._isPlayForFun;
    },

    /**
     * Checks if the game is in idle state.
     *
     * @returns {boolean} Whether or not the game is in idle state.
     */
    isIdle: function() {
        return this._isIdle;
    },

    /**
     * Checks if game history is available for the player.
     *
     * @returns {boolean} Whether or not the game history is available.
     */
    isGameHistoryAvailable: function() {
        return this._isGameHistoryAvailable;
    },

    /**
     * Gets the current platform.
     *
     * @returns {string} The current platform ("desktop", "tablet", "mobile").
     */
    getCurrentPlatform: function() {
        if (Platform.isDesktopDevice) {
            return "desktop";
        }
        else if (Platform.isTabletDevice) {
            return "tablet";
        }

        return "mobile";
    },

    /**
     * Navigates to the lobby.
     *
     * @param {number} [reason] The reason (a decimal integer) given for going to lobby.
     * @returns {void}
     */
    goToLobby: function(reason) {
        Sys.utils.goToLobby(reason);
    },

    /**
     * Checks if home button is currently visible or not.
     *
     * @returns {boolean} Whether or not the home button is visible.
     */
    isHomeButtonVisible: function() {
        return this._homeButtonVisible;
    },

    /**
     * Checks if home button is available to show or not.
     *
     * @returns {boolean} Whether or not the home button is available.
     */
    isHomeButtonAvailable: function() {
        return Sys.utils.isHomeAvailable();
    },

    /**
     * Set the visibility of home button to false.
     *
     * @returns {void}
     */
    hideHomeButton: function() {
        if (this._homeButtonVisible) {
            this._homeButtonVisible = false;
            this.publish("hideHomeButton");
        }
    },

    /**
     * Set the visibility of home button to true.
     *
     * @returns {void}
     */
    showHomeButton: function() {
        if (this.isHomeButtonAvailable()) {
            this._homeButtonVisible = true;
            this.publish("showHomeButton");
        }
    },

    /**
     * Navigates to game history.
     *
     * @returns {void}
     */
    goToHistory: function() {
        var historyUrl = Resources.readData("historyUrl");

        if (!this.isGameHistoryAvailable()) {
            return;
        }

        if (Platform.isDesktopDevice) {
            Sys.utils.openNewBrowserTab(historyUrl, "gameHistory");
        }
        else {
            Sys.utils.setWindowLocation(historyUrl);
        }
    },

    /**
     * Gets the game modes.
     *
     * @returns {string[]} The currently active game modes.
     */
    getGameModes: function() {
        return this._modes;
    },

    /**
     * Adds the game mode (if it does not already exist).
     *
     * @param {string} mode The game mode to add.
     * @returns {void}
     */
    addGameMode: function(mode) {
        if (this._modes.indexOf(mode) > -1) {
            return;
        }

        this._modes.push(mode);
    },

    /**
     * Removes the game mode (if it exist).
     *
     * @param {string} mode The game mode to remove.
     * @returns {void}
     */
    removeGameMode: function(mode) {
        var index = this._modes.indexOf(mode);

        if (index === -1) {
            return;
        }

        this._modes.splice(index, 1);
    },

    /**
     * Handles when the game is ready.
     *
     * @private
     * @returns {void}
     */
    _gameReady: function() {
        this._ready = true;

        this.publish("ready");
    },

    /**
     * Handles when the game mode has changed.
     *
     * @private
     * @param {string} newMode The new game mode.
     * @returns {void}
     */
    _gameModeChanged: function(newMode) {
        if (newMode === "BASIC") {
            this._inBaseGame = true;

            this.publish("enteringMainGame");
            this._setDisplayMode("DEFAULT");
        }
        else if (this._inBaseGame) {
            this._inBaseGame = false;

            this.publish("leavingMainGame");
            this._setDisplayMode("MINIMAL");
        }
        this.publish("modeChanged", newMode);
    },

    /**
     * Handles when the display mode has changed.
     *
     * @private
     * @param {string} mode The display mode.
     * @returns {void}
     */
    _setDisplayMode: function(mode) {
        if (this._currentMode !== mode) {
            this._currentMode = mode;
            this.publish("setDisplayMode", this._currentMode);
        }
    },

    /**
     * Sets display mode when plugin is closed.
     *
     * @private
     * @returns {void}
     */
    _pluginMenuClosed: function() {
        if (this._inBaseGame) {
            this._setDisplayMode("DEFAULT");
        }
        else {
            this._setDisplayMode("MINIMAL");
        }
    },

    /**
     * Handles when the loader is closed.
     *
     * @private
     * @returns {void}
     */
    _onLoaderClosed: function() {
        this._gameReady();
        this.showHomeButton();
    },

    /**
     * Handles when the game enters idle state.
     *
     * @private
     * @returns {void}
     */
    _onEnteringIdle: function() {
        this._isIdle = true;
    },

    /**
     * Handles when the game leaves idle state.
     *
     * @private
     * @returns {void}
     */
    _onLeavingIdle: function() {
        this._isIdle = false;
    },

    /**
     * Stores data after game configuration parsed.
     *
     * @private
     * @param {Object} parsedResponse The parsed response.
     * @param {Object} response The unparsed response.
     * @returns {void}
     */
    _onGameConfigurationParsed: function(parsedResponse, response) {
        Resources.storeData("gameServerInitResponse", Sys.utils.queryStringToObject(response.responseText));
        Resources.storeData("gameServerInitResponseObject", parsedResponse);
        Resources.storeData("unParsedGameServerInitResponse", response.responseText);
    },

    /**
     * Sets the initial state of Max Bet availability.
     *
     * @private
     * @param {Object} response The parsed response.
     * @param {Object} urlParams The parsed url params.
     * @returns {boolean} Initial state of Max Bet availability.
     */
    _setupMaxBetAvailability: function(response, urlParams) {
        var sppValue = this._getGameParametersValue(response, "maxBet"),
            operatorValue = urlParams.maxBet,
            result;

        if (operatorValue !== undefined) {
            result = operatorValue;
        }
        else if (sppValue !== undefined) {
            result = sppValue;
        }
        else {
            result = true;
        }

        return result;
    },

    /**
     * Returns parameter from gameParameters response section.
     *
     * @private
     * @param {Object} response The parsed response.
     * @param {string} parameter Param name.
     * @returns {any|undefined}.
     */
    _getGameParametersValue: function(response, parameter) {
        if (!(typeof response.config === "undefined") && !(typeof response.config.gameParameters === "undefined") && !(typeof response.config.gameParameters[parameter] === "undefined")) {
            return response.config.gameParameters[parameter];
        }
        return undefined;
    },

    /**
     * Checks if QuickSpin is available (allowed) or not.
     *
     * @returns {boolean} Whether or not QuickSpin is available.
     */
    isQuickSpinAvailable: function() {
        return this._isQuickSpinAvailable;
    },

    /**
     * Checks if MaxBet is available (allowed) or not.
     *
     * @returns {boolean} Whether or not MaxBet is available.
     */
    isMaxBetAvailable: function() {
        return this._isMaxBetAvailable;
    }
};

Services.Game = Sys.extend(Services.Base, Services.Game, "Services.Game");
